import { AnswerPhoto } from '../../models/message';

/** The most photos an answer's gallery shows. */
export const GALLERY_MAX = 8;
/** A card's shape (width over height) when its photo's size is unknown. */
export const DEFAULT_RATIO = 3 / 2;
/** The shapes a card keeps within, so the row stays calm: from square to wide. */
const MIN_RATIO = 1;
const MAX_RATIO = 16 / 9;

/** One card of the gallery: a photo, its place's name in bold, and the hotel's caption when it says more. */
export interface GalleryCard {
   photo: AnswerPhoto;
   title: string;
   caption?: string;
   alt: string;
   /** Width over height, so the card holds its space before the photo loads. */
   ratio: number;
}

/** The photos by place, in the order they came (the server groups a place's photos and orders places as the answer names them). */
export function groupByPlace(photos: AnswerPhoto[]): AnswerPhoto[][] {
   const places = new Map<string, AnswerPhoto[]>();
   for (const photo of photos) {
      const key = photo.place ?? `${photo.kind}:${photo.title}`;
      places.set(key, [...(places.get(key) ?? []), photo]);
   }
   return Array.from(places.values());
}

function toCard(photo: AnswerPhoto): GalleryCard {
   const known = !!photo.width && !!photo.height;
   const ratio = known ? Math.min(MAX_RATIO, Math.max(MIN_RATIO, photo.width! / photo.height!)) : DEFAULT_RATIO;
   const card: GalleryCard = { photo, title: photo.title, alt: photo.alt || photo.title, ratio };
   const caption = photo.caption?.trim();
   if (caption && caption.toLocaleLowerCase() !== photo.title.trim().toLocaleLowerCase()) card.caption = caption;
   return card;
}

/**
 * What an answer's gallery shows: first one photo of each place, in the order the answer names them, then the places'
 * further photos in the order they came, at most `GALLERY_MAX` in all.
 */
export function galleryCards(photos: AnswerPhoto[], max = GALLERY_MAX): GalleryCard[] {
   const places = groupByPlace(photos);
   const firsts = places.map((place) => place[0]);
   const further = places.reduce<AnswerPhoto[]>((rest, place) => rest.concat(place.slice(1)), []);
   return firsts.concat(further).slice(0, max).map(toCard);
}
